import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { CreditRiskAPI, formatCurrency, formatPercentage } from '../services/api';
import { SumaRefinanceListResult, SumaRefinanceListRow } from '../types';

interface SumaRefinanceListProps {
  onOpenClient: (clienteId: string) => void;
}

const TIER_COLOR: Record<string, string> = {
  ELEGIBLE_PREFERENTE: '#2e7d32',
  ELEGIBLE_CON_REVISION: '#ed6c02',
  NO_ELEGIBLE: '#d32f2f',
  REQUIERE_DATOS: '#0288d1',
  FUERA_DE_ALCANCE: '#5e35b1',
};

const TIER_LABEL: Record<string, string> = {
  ELEGIBLE_PREFERENTE: 'Elegible preferente',
  ELEGIBLE_CON_REVISION: 'Elegible con revisión',
  NO_ELEGIBLE: 'No elegible',
  REQUIERE_DATOS: 'Requiere datos',
  FUERA_DE_ALCANCE: 'Fuera de alcance',
};

const money = (value?: number | null): string =>
  value == null ? '—' : formatCurrency(value);

const pct = (value?: number | null): string =>
  value == null ? '—' : formatPercentage(value);

const text = (value?: string | number | null): string =>
  value == null || value === '' ? '—' : String(value);

const SumaRefinanceList: React.FC<SumaRefinanceListProps> = ({ onOpenClient }) => {
  const [payload, setPayload] = useState<SumaRefinanceListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const response = await CreditRiskAPI.getSumaRefinanceList();
      if (cancelled) return;
      if (response.success && response.data) {
        setPayload(response.data);
        setError(null);
      } else {
        setError(response.error?.message || 'No se pudo cargar la lista SUMA');
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const all = payload?.rows || [];
    const needle = query.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((row) =>
      [
        row.cliente_id,
        row.credito_id,
        row.provincia,
        row.sector_general,
        row.refi_tier,
        row.suma_banda,
        row.scorecard_banda,
        row.refi_banda,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [payload, query]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 6 }}>
        <CircularProgress size={22} />
        <Typography color="text.secondary">Cargando la lista que envió SUMA…</Typography>
      </Box>
    );
  }

  if (error || !payload) {
    return <Alert severity="error">{error || 'Lista no disponible'}</Alert>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Lista SUMA · {payload.n} clientes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {payload.note} {payload.n_with_refinance} tienen decisión de refinanciamiento
        en la cartera vigente.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar cliente, crédito, provincia, banda o elegibilidad"
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 360, flexGrow: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          Mostrando {rows.length} de {payload.n}
        </Typography>
      </Stack>

      <TableContainer sx={{ maxHeight: 640 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: '#eceff1', fontWeight: 700 }}>
                Identidad
              </TableCell>
              <TableCell colSpan={8} sx={{ bgcolor: '#e3f2fd', fontWeight: 700 }}>
                Datos SUMA
              </TableCell>
              <TableCell colSpan={3} sx={{ bgcolor: '#fff3e0', fontWeight: 700 }}>
                Score SUMA (sistema anterior)
              </TableCell>
              <TableCell colSpan={4} sx={{ bgcolor: '#e8f5e9', fontWeight: 700 }}>
                Nuestro scorecard
              </TableCell>
              <TableCell colSpan={7} sx={{ bgcolor: '#f3e5f5', fontWeight: 700 }}>
                Refinanciamiento
              </TableCell>
              <TableCell sx={{ bgcolor: '#fafafa' }} />
            </TableRow>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Crédito</TableCell>
              <TableCell>Edad</TableCell>
              <TableCell>Género</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Plazo</TableCell>
              <TableCell>Tasa</TableCell>
              <TableCell>Ingreso</TableCell>
              <TableCell>Segmento</TableCell>
              <TableCell>Provincia</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Banda</TableCell>
              <TableCell>PD</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Banda</TableCell>
              <TableCell>PD</TableCell>
              <TableCell>PD mod. 1</TableCell>
              <TableCell>Elegibilidad</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Banda</TableCell>
              <TableCell>PD</TableCell>
              <TableCell>Atraso</TableCell>
              <TableCell>Cartera</TableCell>
              <TableCell>Reglas</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: SumaRefinanceListRow) => (
              <TableRow key={`${row.cliente_id}-${row.credito_id}`} hover>
                <TableCell>{text(row.cliente_id)}</TableCell>
                <TableCell>{text(row.credito_id)}</TableCell>
                <TableCell>{text(row.edad)}</TableCell>
                <TableCell>{text(row.genero)}</TableCell>
                <TableCell>{money(row.monto_credito)}</TableCell>
                <TableCell>{text(row.plazo)}</TableCell>
                <TableCell>{row.tasa_fija == null ? '—' : `${row.tasa_fija}%`}</TableCell>
                <TableCell>{money(row.ingreso_mensual)}</TableCell>
                <TableCell>{text(row.sector_general)}</TableCell>
                <TableCell>{text(row.provincia)}</TableCell>
                <TableCell>{text(row.suma_score)}</TableCell>
                <TableCell>{text(row.suma_banda)}</TableCell>
                <TableCell>{pct(row.suma_probabilidad)}</TableCell>
                <TableCell>{text(row.scorecard_score)}</TableCell>
                <TableCell>{text(row.scorecard_banda)}</TableCell>
                <TableCell>{pct(row.scorecard_pd)}</TableCell>
                <TableCell>{pct(row.model1_pd)}</TableCell>
                <TableCell>
                  {row.refi_tier ? (
                    <Chip
                      size="small"
                      label={TIER_LABEL[row.refi_tier] || row.refi_tier}
                      sx={{
                        bgcolor: TIER_COLOR[row.refi_tier] || '#616161',
                        color: 'white',
                      }}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{text(row.refi_score)}</TableCell>
                <TableCell>{text(row.refi_banda)}</TableCell>
                <TableCell>{pct(row.refi_pd)}</TableCell>
                <TableCell>{text(row.refi_dias_atraso)}</TableCell>
                <TableCell>{text(row.refi_segmento)}</TableCell>
                <TableCell sx={{ maxWidth: 280, whiteSpace: 'normal' }}>
                  {text(row.refi_reglas)}
                </TableCell>
                <TableCell>
                  {row.cliente_id && (
                    <Button size="small" onClick={() => onOpenClient(row.cliente_id as string)}>
                      Evaluar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default SumaRefinanceList;
